import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { Card, Form, Col } from "react-bootstrap";
import useMessage from "hooks/useMessage";
import useExceptionHandler from "hooks/useExceptionHandler";
import { Simplebar } from "components";
import DropZone from "components/DropZone/Dropzone";
import { CategoriaLookup } from "components/Lookups/CategoriaLookup/CategoriaLookup";
import IconButton from "components/IconButton/IconButton";
import SectionRow from "components/PantallasEdit/SectionRow/SectionRow";
import EditContainer from "components/PantallasEdit/EditContainer/EditContainer";
import InputContainer from "components/PantallasEdit/InputContainer/InputContainer";
import EditSection from "components/PantallasEdit/EditSection/EditSection";
import { ItemManager } from "managers";
import { Item } from "models/ECF/Item";

export default function ItemEdit(
  {
    showLoading,
    hideLoading,
    IdSec = undefined,
    showCanvas,
    setShowCanvas,
    updateList = () => { },
  }) {

  const [esNuevo, setEsNuevo] = useState(!IdSec);
  const [item, setItem] = useState({ Estatus: Item.DEFAULT_ESTATUS_VALUE });
  const [tituloHeader, setTituloHeader] = useState("Crear Item");
  const ref = useRef(null);
  const { Message, cerrarAlertas } = useMessage();
  const { handleException } = useExceptionHandler();

  useEffect(() => {
    if (IdSec) {
      showLoading();
      cargarItem(IdSec);
      setTituloHeader("Editar Item");
    } else {
      setDefaultValues();
      setTituloHeader("Crear Item");
    }
    hideLoading();
    cerrarAlertas();
    return () => { };
  }, [IdSec]);

  const setDefaultValues = () => {
    setItem({
      Nombre: "",
      Descripcion: "",
      IndicadorFacturacion: "",
      IndicadorBienOServicio: "",
      PrecioUnitario: "",
      Estatus: Item.DEFAULT_ESTATUS_VALUE,
      Img: [],
    });
  };

  const cargarItem = (IdSec) => {
    showLoading();
    ItemManager.buscarPorId(IdSec).then((data) => {
      if (data && data.IdSec) {
        setItem({
          ...data,
          Img: [],
          Estatus: data.Estatus ?? Item.DEFAULT_ESTATUS_VALUE,
        });
      } else {
        Message({ message: "Item no encontrado", type: "error" });
      }
    })
      .catch((e) => handleException(e))
      .finally(() => {
        hideLoading();
        updateList();
      }
      );
  };

  function grabar() {
    showLoading();

    const payload = {
      ...item,
      Estatus: item.Estatus ?? Item.DEFAULT_ESTATUS_VALUE,
    };

    if (esNuevo) {
      ItemManager.crear(payload)
        .then((_res) => {
          setEsNuevo(false);
          cargarItem(_res.IdSec);
          IdSec = _res.IdSec;
          Message({ message: `Grabado Exitosamente` });
        })
        .catch((e) => handleException(e))
        .finally(() => {
          hideLoading();
          updateList();
        });
    } else {
      ItemManager.actualizar(payload)
        .then((res) => {
          Message({ message: `Actualizado Exitosamente` });
          cargarItem(payload.IdSec);
        })
        .catch((e) => handleException(e))
        .finally(() => {
          hideLoading();
          updateList();
        });
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setItem({
      ...item,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleCategoriaChange = (event) => {
    if (event && event.value && event.value !== item.CategoriaIdSec) {
      setItem({
        ...item,
        CategoriaId: event.value,
      });
    } else {
      setItem({
        ...item,
        CategoriaId: undefined,
      });
    }
  };

  const onFileChange = (files) => {
    setItem({ ...item, Img: files, _EliminarImagen: (files?.length ?? 0) === 0, ImagenBase64: null });
  };

  return (
    <Simplebar
      ref={ref}
      backdrop={"static"}
      confirmBack
      placement={"end"}
      size="xxl"
      portal={false}
      visible={showCanvas}
      setShow={(e) => {
        setShowCanvas(e);
      }}
      useLoading={true}
      title={tituloHeader}
      btnLeft={{
        show: true,
        message: "Grabar",
        isComponent: true,
        size: "45px",
        height: "24px",
        with: "24px",
        iconName: "save",
        isCenter: true,
        func: grabar,
      }}
      btnRight={{
        show: false,
        label: "Grabar",
        func: () => { },
      }}
    >
      <Col>
        <Form action="" className="form-horizontal" method="">

          <EditContainer>
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              {item?.ImagenBase64 ? (
                <InputContainer
                  labelClassName={'d-flex'}
                  rowStyle={{ width: '100%', display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}
                  style={{ display: 'flex', justifyContent: 'center', width: '100%', textAlign: 'center' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div >
                      <img src={
                        item.ImagenBase64?.startsWith("data:image")
                          ? item.ImagenBase64
                          : `data:image/png;base64,${item.ImagenBase64}`
                      } alt="Imagen seleccionada"
                        style={{ maxWidth: "300px", maxHeight: "300px" }} />
                    </div>

                    <IconButton styles={{ height: '2.5rem' }}  >
                      <i className="fa fa-trash" onClick={() => setItem({ ...item, ImagenBase64: null, _EliminarImagen: true })}></i>
                    </IconButton>
                  </div>

                </InputContainer>
              ) : (
                <InputContainer
                  label="Imagen"
                  labelClassName={'d-flex'}
                  rowStyle={{ display: 'flex', justifyContent: 'center', width: '50%', marginBottom: '1rem' }}
                  style={{ display: 'flex', justifyContent: 'center', width: '100%', textAlign: 'center' }}>
                  <DropZone
                    label="Seleccionar Imagen"
                    updateFilesCb={onFileChange}
                    multiple={false}
                    type="image" />
                </InputContainer>
              )}
            </div>

            <SectionRow>
              <InputContainer label="Nombre" labelClassName={'d-flex'} required>
                <Form.Control
                  type="text"
                  name="Nombre"
                  value={item.Nombre}
                  onChange={handleChange}
                />
              </InputContainer>
              <InputContainer label="Categoría" labelClassName={'d-flex'}>
                <CategoriaLookup
                  initialValue={{ value: item.CategoriaId }}
                  onChange={(e) => handleCategoriaChange(e)}
                />
              </InputContainer>
            </SectionRow>

            <SectionRow>
              <InputContainer label="Descripción" labelClassName={'d-flex'}>
                <Form.Control
                  type="text"
                  name="Descripcion"
                  value={item.Descripcion}
                  onChange={handleChange}
                />
              </InputContainer>
              <InputContainer label="Estatus" labelClassName={'d-flex'}>
                <Select
                  className="react-select primary"
                  classNamePrefix="react-select"
                  name="Estatus"
                  options={Item.ESTATUS_LIST}
                  value={Item.ESTATUS_LIST.find(function (option) {
                    return (
                      option.value == (item.Estatus ?? Item.ESTATUS_LIST[0].value)
                    );
                  })}
                  onChange={(e) => {
                    setItem({
                      ...item,
                      Estatus: e.value,
                    });
                  }}
                  isDisabled={esNuevo}
                />
              </InputContainer>
            </SectionRow>

            <SectionRow>
              <InputContainer label="Precio" labelClassName={'d-flex'} required>
                <Form.Control
                  type="number"
                  name="PrecioUnitario"
                  value={item.PrecioUnitario}
                  placeholder="Precio Unitario"
                  onChange={handleChange}
                />
              </InputContainer>
              <InputContainer label="Código de Barra" labelClassName={'d-flex'}>
                <Form.Control
                  type="text"
                  name="CodigoBarra"
                  value={item.CodigoBarra}
                  onChange={handleChange}
                />
              </InputContainer>
            </SectionRow>

            <SectionRow>
              <InputContainer label="Tipo" labelClassName={'d-flex'} required>
                <Select
                  className="react-select primary"
                  classNamePrefix="react-select"
                  name="IndicadorBienOServicio"
                  options={Item.INDICADOR_BIEN_O_SERVICIO_LIST}
                  value={Item.INDICADOR_BIEN_O_SERVICIO_LIST.find(function (option) {
                    return (
                      option.value === item.IndicadorBienOServicio
                    );
                  })}
                  onChange={(e) => {
                    setItem({
                      ...item,
                      IndicadorBienOServicio: e.value
                    });
                  }}
                />
              </InputContainer>

              <InputContainer label="Tipo ITBIS" labelClassName={'d-flex'} required>
                <Select
                  className="react-select primary"
                  classNamePrefix="react-select"
                  name="IndicadorFacturacion"
                  options={Item.INDICADOR_FACTURACION_LIST}
                  value={Item.INDICADOR_FACTURACION_LIST.find(function (option) {
                    return (
                      option.value === item.IndicadorFacturacion
                    );
                  })}
                  onChange={(e) => {
                    setItem({
                      ...item,
                      IndicadorFacturacion: e.value
                    });
                  }}
                />
              </InputContainer>
            </SectionRow>

          </EditContainer>
        </Form>
      </Col>
    </Simplebar>
  );
}